import React, { createContext, useContext, useRef, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useRouteLoaderData } from "react-router-dom";

import { tmrPushImageEditorEvent } from "@components/Counters";
import { DevModal } from "@components/Helpers";

const stepQuality = 0.01;
const ImageCropperContext = createContext();

export function useImageCropperModal() {
    return useContext(ImageCropperContext);
}

function calcVolume(dataurl) {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[arr.length - 1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    const file = new File([u8arr], "tmp", { type: mime });
    return Math.round(file.size / 1000);
}

export function ImageCropperModalProvider({ apiData, setApiData, children }) {
    const { t } = useTranslation();

    const imgRefs = useRef([]);
    const previewOriginalRef = useRef(null);
    const previewResultRef = useRef(null);
    const qualityRef = useRef(null);

    const [formData, setFormData] = useState(null);
    const [formPrefix, setFormPrefix] = useState(null);
    const [formFieldName, setFormFeldName] = useState(null);
    const [formFieldRef, setFormFieldRef] = useState(null);
    const [imgData, setImgData] = useState([]);
    const [editInd, setEditInd] = useState(null);

    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    function getBase64(image, x, y, width, height, mime, quality, scale = 1) {
        const cropX = Math.round(x * scale);
        const cropY = Math.round(y * scale);
        const cropWidth = Math.round(width * scale);
        const cropHeight = Math.round(height * scale);

        const canvas = document.createElement("canvas");
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

        return canvas.toDataURL(mime, quality);
    }

    const initHints = (prefix, fieldName, obj) => {
        const { max_volume } = apiData.forms_data[prefix].fields[fieldName].widget.attrs.image_presets[0];
        const volume = obj.croppedVolume || obj.originalVolume;
        return volume > max_volume
            ? [
                  {
                      variant: "warning",
                      text: t(
                          "Размер изображения слишком большой ({{volume}} > {{max_volume}}). Сожмите файл, уменьшив его качество в редакторе.",
                          { volume, max_volume }
                      ),
                  },
              ]
            : [];
    };

    const showImageCropperModal = (contents, prefix, fieldName, fieldRef, form) => {
        setFormData(form);
        setFormPrefix(prefix);
        setFormFeldName(fieldName);
        setFormFieldRef(fieldRef);

        const newImgData = [];
        contents
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach((obj) => {
                const originalFormat = /^data:(.*?);base64,/.exec(obj.originalSrc);
                const fileType = originalFormat[1].split("/")[1];
                const { x, y, format, check } =
                    apiData.forms_data[prefix].fields[fieldName].widget.attrs.image_presets[0];
                const reqFormat = format ? format.split(",") : [];
                const requiredAspect = x / y;
                const imgWidth = obj.img.naturalWidth;
                const imgHeight = obj.img.naturalHeight;
                const imgAspect = imgWidth / imgHeight;

                if (!reqFormat || reqFormat.includes(fileType)) {
                    obj.imageType = fileType;
                } else {
                    obj.imageType = reqFormat[0];
                }

                obj.quality = 0.92;
                obj.MIMEType = "image/" + obj.imageType;
                obj.originalSrc = getBase64(obj.img, 0, 0, obj.img.width, obj.img.height, obj.MIMEType, obj.quality);
                obj.originalVolume = calcVolume(obj.originalSrc);
                obj.hints = initHints(prefix, fieldName, obj);

                if (!originalFormat) {
                    obj.editorDisabled = true;
                    obj.hints = [{ variant: "danger", text: t("Неподдерживаемый формат файла") }];
                    newImgData.push(obj);
                    return;
                }

                if (fileType.includes("icon") && reqFormat.includes("ico")) {
                    obj.editorDisabled = true;
                    newImgData.push(obj);
                    return;
                }

                const successFormat = ["jpg", "jpeg", "png", "webp"];
                if (!successFormat.includes(fileType)) {
                    obj.editorDisabled = true;
                    obj.hints = [
                        {
                            variant: "danger",
                            text: t(
                                "Неподдерживаемый формат файла. Формат файла: " +
                                    fileType +
                                    " не соответствует формату поля: " +
                                    successFormat.toString()
                            ),
                        },
                    ];
                    newImgData.push(obj);
                    return;
                }

                if (check === "max") {
                    newImgData.push(obj);
                    return;
                } else if (imgWidth < x || imgHeight < y) {
                    obj.editorDisabled = true;
                    obj.hints.push({
                        variant: "danger",
                        text: t(
                            "Разрешение изображения ниже допустимого: требуется не менее " +
                                (x || "∞") +
                                "x" +
                                (y || "∞") +
                                ". " +
                                "Текущее: " +
                                (imgWidth || "∞") +
                                "x" +
                                (imgHeight || "∞")
                        ),
                    });
                    newImgData.push(obj);
                    return;
                }

                if (check === "exact") {
                    if (imgWidth === x && imgHeight === y) {
                        newImgData.push(obj);
                        return;
                    } else if (
                        (x && x <= imgWidth && y && y <= imgHeight && requiredAspect === imgAspect) ||
                        (x && x <= imgWidth && !y) ||
                        (y && y <= imgHeight && !x)
                    ) {
                        newImgData.push(obj);
                        return;
                    }
                } else if (check === "between_exact") {
                    if (imgWidth === x && imgHeight === y) {
                        newImgData.push(obj);
                        return;
                    }
                }

                if (imgWidth >= x && imgHeight >= y) {
                    if (requiredAspect === imgAspect) {
                        newImgData.push(obj);
                        return;
                    }
                    obj.hints.push({
                        variant: "warning",
                        text: t("Неправильные пропорции. Обрезать изображение можно в редакторе."),
                    });
                    newImgData.push(obj);
                    return;
                }

                // все неучтенные случаи
                obj.hints = [{ variant: "danger", text: t("Ошибка") }];
                newImgData.push(obj);
            });
        setImgData(newImgData);
    };

    const saveToForm = async () => {
        async function resizeBase64Image(base64, width, height, mimeType, quality) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = function () {
                    const canvas = document.createElement("canvas");
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, width, height);
                    const resizedBase64 = canvas.toDataURL(mimeType, quality);
                    resolve(resizedBase64);
                };
                img.onerror = reject;
                img.src = base64;
            });
        }

        const prepared = imgData
            .filter((obj) => obj.hints.length === 0)
            .map(async (obj, ind) => {
                const image = imgRefs.current[ind];
                const width = obj.croppedWidth || image.naturalWidth;
                const height = obj.croppedHeight || image.naturalHeight;
                const { x, y, x_2, y_2, check } =
                    apiData.forms_data[formPrefix].fields[formFieldName].widget.attrs.image_presets[0];

                let newW = 0;
                let newH = 0;
                if (check === "max") {
                    if (width <= x && height <= y) {
                        newW = parseInt(width);
                        newH = parseInt(height);
                    } else {
                        const scale_w = x / width;
                        const scale_h = y / height;
                        const scale = Math.min(scale_w, scale_h, 1.0);
                        newW = parseInt(width * scale);
                        newH = parseInt(height * scale);
                    }
                } else if (check === "exact") {
                    if (x && y) {
                        newW = parseInt(x);
                        newH = parseInt(y);
                    } else if (x) {
                        newW = parseInt(x);
                        if (width > x) {
                            const scale = x / width;
                            newH = parseInt(height * scale);
                        } else {
                            newH = parseInt(height);
                        }
                    } else if (y) {
                        newH = parseInt(y);
                        if (height > y) {
                            const scale = y / height;
                            newW = parseInt(width * scale);
                        } else {
                            newW = parseInt(width);
                        }
                    }
                } else if (check === "between") {
                    if (x <= width && width < x_2 && y <= height && height < y_2) {
                        newW = parseInt(width);
                        newH = parseInt(height);
                    } else {
                        newW = parseInt(x_2);
                        newH = parseInt(y_2);
                    }
                } else if (check === "between_exact") {
                    if (x <= width && width < x_2 && y <= height && height < y_2) {
                        newW = parseInt(x);
                        newH = parseInt(y);
                    } else {
                        newW = parseInt(x_2);
                        newH = parseInt(y_2);
                    }
                } else {
                    newW = parseInt(x);
                    newH = parseInt(y);
                }

                if (!newW || !newH) {
                    return;
                }

                const resizedBase64 = await resizeBase64Image(image.src, newW, newH, obj.MIMEType, obj.quality);
                const res = await fetch(resizedBase64);
                const blob = await res.blob();
                return new File([blob], `cropped_${ind}.${obj.imageType}`, { type: obj.MIMEType });
            });

        const newApiData = { ...apiData };
        newApiData.forms_data[formPrefix].data[formFieldName] = await Promise.all(prepared);
        setApiData(newApiData);
        setImgData([]);
    };

    const saveToPreview = async (objInd) => {
        const newImgData = [...imgData];
        newImgData[objInd].hints = initHints(formPrefix, formFieldName, newImgData[objInd]);
        newImgData[objInd].croppedSrc = previewResultRef.current.src;
        setImgData(newImgData);
        setEditInd(null);
        tmrPushImageEditorEvent("img_save_click", gameCommonInfo, formData);
    };

    const updateCropInfo = (frame) => {
        const ref = previewOriginalRef.current;
        const scale = ref.naturalWidth / ref.clientWidth;
        const quality = parseFloat(qualityRef.current.value);

        let data = { frame };
        if (!frame) {
            const { x, y, check } = apiData.forms_data[formPrefix].fields[formFieldName].widget.attrs.image_presets[0];
            let cropWidth = x;
            let cropHeight = y;
            let aspect = cropWidth && cropHeight ? cropWidth / cropHeight : undefined;

            let minWidthCrop = 1;
            let minHeightCrop = 1;
            if (check === "max") {
                cropWidth = 0;
                cropHeight = 0;
                aspect = undefined;
            }
            if (cropWidth) {
                minWidthCrop = cropWidth / scale;
            }
            if (cropHeight) {
                minHeightCrop = cropHeight / scale;
            }

            if (cropWidth && scale > 1) {
                cropWidth = cropWidth / scale;
            }
            if (cropHeight && scale > 1) {
                cropHeight = cropHeight / scale;
            }

            frame = {
                unit: "px",
                width: cropWidth > 0 ? cropWidth : ref.clientWidth,
                height: cropHeight > 0 ? cropHeight : ref.clientHeight,
            };

            data = { frame, minWidthCrop, minHeightCrop, aspect };
        }

        previewResultRef.current.src = getBase64(
            imgData[editInd].img,
            frame.x || 0,
            frame.y || 0,
            frame.width,
            frame.height,
            imgData[editInd].MIMEType,
            quality,
            previewOriginalRef.current.naturalWidth / previewOriginalRef.current.width
        );

        setImgData((items) =>
            items.map((obj, ind) =>
                ind === editInd
                    ? {
                          ...obj,
                          ...data,
                          quality,
                          croppedWidth: Math.round(frame.width * scale),
                          croppedHeight: Math.round(frame.height * scale),
                          croppedVolume: calcVolume(previewResultRef.current.src),
                      }
                    : obj
            )
        );
    };

    const closeImageEditor = () => {
        const newApiData = { ...apiData };
        newApiData.forms_data[formPrefix].data[formFieldName] = null;
        setApiData(newApiData);
        formFieldRef.value = "";
        setImgData([]);

        // Очистить имя файла в Fields.jsx
        const event = new CustomEvent("closeImageCropperModal", { detail: { fieldName: formFieldName } });
        window.dispatchEvent(event);
    };

    const closeCropper = () => {
        tmrPushImageEditorEvent("img_close_click", gameCommonInfo, formData);
        setEditInd(null);
    };

    return (
        <ImageCropperContext.Provider value={{ showImageCropperModal }}>
            {children}

            <DevModal
                show={imgData.length}
                onHide={closeImageEditor}
                header={t("Редактор изображений")}
                body={imgData.map((obj, ind) => {
                    return (
                        <div key={`editorimg_${ind}`} className="my-2">
                            <div className="d-flex flex-column align-items-center">
                                <p>
                                    <b>{obj.name}</b>
                                </p>
                                <img
                                    ref={(el) => (imgRefs.current[ind] = el)}
                                    src={obj.croppedSrc || obj.originalSrc}
                                    className="img-fluid"
                                />
                                <div className="mb-3">
                                    {t("Размер")}: {obj.croppedWidth || obj.img.naturalWidth}x
                                    {obj.croppedHeight || obj.img.naturalHeight} [
                                    {obj.croppedVolume || obj.originalVolume} kb]
                                </div>
                            </div>
                            {obj.hints.length > 0 ? (
                                obj.hints.map((h, hind) => {
                                    return (
                                        <Alert key={`hint_${hind}`} variant={h.variant}>
                                            {h.text}
                                        </Alert>
                                    );
                                })
                            ) : (
                                <Alert variant="success">{t("Изображение готово к загрузке")}</Alert>
                            )}
                            <Button
                                variant="primary"
                                block
                                onClick={() => {
                                    setEditInd(ind);
                                    tmrPushImageEditorEvent("img_open_editor_click", gameCommonInfo, formData);
                                }}
                                disabled={obj.editorDisabled}
                            >
                                {t("Открыть редактор изображения")}
                            </Button>
                        </div>
                    );
                })}
                footer={
                    <Button
                        variant="primary"
                        onClick={saveToForm}
                        disabled={!imgData.filter((obj) => obj.hints.length === 0).length}
                    >
                        {t("Загрузить")}
                    </Button>
                }
            />

            <DevModal
                size="xl"
                show={editInd !== null}
                onHide={closeCropper}
                header={t("Измените размеры изображения")}
                body={
                    editInd !== null && (
                        <>
                            <div className="d-flex flex-column align-items-center">
                                <ReactCrop
                                    onChange={(frame) => updateCropInfo(frame)}
                                    keepSelection={true}
                                    crop={imgData[editInd].frame}
                                    aspect={imgData[editInd].aspect}
                                    minWidth={imgData[editInd].minWidthCrop}
                                    minHeight={imgData[editInd].minHeightCrop}
                                >
                                    <img
                                        ref={previewOriginalRef}
                                        className="img-fluid"
                                        src={imgData[editInd].originalSrc}
                                        onLoad={() => updateCropInfo(imgData[editInd].frame)}
                                    />
                                </ReactCrop>
                            </div>
                            <Row className="my-4">
                                <Col md={1}>{t("Качество")}:</Col>
                                <Col md={10} className="d-flex justify-content-center">
                                    <Form.Control
                                        ref={qualityRef}
                                        type="range"
                                        min={stepQuality}
                                        max="1"
                                        step={stepQuality}
                                        value={imgData[editInd].quality}
                                        onChange={() => updateCropInfo(imgData[editInd].frame)}
                                    />
                                </Col>
                                <Col md={1}>{Math.trunc(imgData[editInd].quality * 100)}%</Col>
                            </Row>
                            <div className="d-flex flex-column align-items-center">
                                <div>
                                    <b>
                                        {imgData[editInd].croppedWidth}x{imgData[editInd].croppedHeight} [
                                        {imgData[editInd].croppedVolume} kb]
                                    </b>
                                </div>
                                <img
                                    ref={previewResultRef}
                                    style={{
                                        width: imgData[editInd].frame?.width,
                                        height: imgData[editInd].frame?.height,
                                    }}
                                />
                            </div>
                        </>
                    )
                }
                footer={
                    <Button variant="primary" onClick={() => saveToPreview(editInd)}>
                        {t("Сохранить и продолжить")}
                    </Button>
                }
            />
        </ImageCropperContext.Provider>
    );
}
